import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  Alert,
  Collapse,
} from '@mui/material';
import {
  Delete,
  // Edit, // Removed as it is unused
  PlayArrow,
  CheckCircle,
  Link as LinkIcon,
  Archive,
  Unarchive,
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { matrixConfig, Task } from '../config/n8n.config';
import n8nService from '../services/n8nService';
import Linkify from '../utils/linkify';

interface EisenhowerMatrixProps {
  tasks: Task[];
  onTasksChange?: (tasks: Task[]) => void;
  onError?: (error: string) => void;
  showArchived?: boolean;
  onArchiveToggle?: (taskId: string, archived: boolean) => void;
}

interface QuadrantProps {
  quadrantId: keyof typeof matrixConfig.quadrants;
  tasks: Task[];
  onTaskMove: (taskId: string, newQuadrant: string) => void;
  onTaskDelete: (taskId: string) => void;
  onTaskStatusChange: (taskId: string, newStatus: Task['status']) => void;
  showArchived?: boolean;
  onArchiveToggle?: (taskId: string, archived: boolean) => void;
}

interface TaskCardProps {
  task: Task;
  onDelete: (taskId: string) => void;
  onStatusChange: (taskId: string, newStatus: Task['status']) => void;
  showArchived?: boolean;
  onArchiveToggle?: (taskId: string, archived: boolean) => void;
}

const ITEM_TYPE = 'TASK';

// Individual Task Card Component
const TaskCard: React.FC<TaskCardProps> = ({ task, onDelete, onStatusChange, showArchived, onArchiveToggle }) => {
  const [expanded, setExpanded] = useState(false);
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { id: task.id, quadrant: task.quadrant, expanded },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const dragRef = drag as any;

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: Task['status']) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'in-progress': return <PlayArrow />;
      default: return undefined;
    }
  };

  const handleStatusClick = () => {
    const statusOrder: Task['status'][] = ['pending', 'in-progress', 'completed'];
    const currentIndex = statusOrder.indexOf(task.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length];
    onStatusChange(task.id, nextStatus);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <div ref={dragRef}>
      <Card
        sx={{
          mb: 1,
          opacity: isDragging ? 0.5 : 1,
          cursor: 'move',
          transition: 'min-height 0.3s',
          '&:hover': {
            boxShadow: 3,
          },
          minHeight: { xs: 80, sm: 'auto' },
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                flex: 1,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: 1.3,
              }}
            >
              {task.title}
            </Typography>
            <Box display="flex" gap={0.5}>
              {onArchiveToggle && (
                <IconButton
                  size="small"
                  onClick={() => onArchiveToggle(task.id, !showArchived)}
                  sx={{ p: { xs: 0.5, sm: 1 } }}
                  title={showArchived ? 'Unarchive task' : 'Archive task'}
                >
                  {showArchived ? (
                    <Unarchive sx={{ fontSize: { xs: 16, sm: 20 } }} />
                  ) : (
                    <Archive sx={{ fontSize: { xs: 16, sm: 20 } }} />
                  )}
                </IconButton>
              )}
              <IconButton
                size="small"
                onClick={() => onDelete(task.id)}
                sx={{ ml: 0.5, p: { xs: 0.5, sm: 1 } }}
              >
                <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </IconButton>
            </Box>
          </Box>

          {!expanded && task.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 0.5,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              <Linkify text={task.description} />
            </Typography>
          )}

          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={0.5}>
            <Chip
              label={task.status}
              size="small"
              color={getStatusColor(task.status)}
              {...(getStatusIcon(task.status) && { icon: getStatusIcon(task.status) })}
              onClick={handleStatusClick}
              sx={{
                cursor: 'pointer',
                fontSize: { xs: '0.7rem', sm: '0.875rem' },
                height: { xs: 20, sm: 24 },
              }}
            />

            {task.voiceNoteUrl && (
              <Chip
                label="Voice"
                size="small"
                variant="outlined"
                color="primary"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  height: { xs: 20, sm: 24 },
                }}
              />
            )}

            {task.link && (
              <Chip
                icon={<LinkIcon />}
                label="Link"
                size="small"
                variant="outlined"
                onClick={handleExpandClick}
                sx={{
                  cursor: 'pointer',
                  fontSize: { xs: '0.7rem', sm: '0.875rem' },
                  height: { xs: 20, sm: 24 },
                }}
              />
            )}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mt: 0.5,
              fontSize: { xs: '0.65rem', sm: '0.75rem' },
            }}
          >
            {task.createdAt || (task as any).created_at
              ? new Date(task.createdAt || (task as any).created_at).toLocaleDateString()
              : 'No date'}
          </Typography>
        </CardContent>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <CardContent sx={{ pt: 0 }}>
            {task.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <Linkify text={task.description} />
              </Typography>
            )}
            {task.link && (
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  width: '100%',
                  pt: '56.25%', // 16:9 Aspect Ratio
                }}
              >
                <iframe
                  src={task.link}
                  title={task.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>
    </div>
  );
};

// Quadrant Component
const Quadrant: React.FC<QuadrantProps> = ({
  quadrantId,
  tasks,
  onTaskMove,
  onTaskDelete,
  onTaskStatusChange,
  showArchived,
  onArchiveToggle,
}) => {
  const quadrant = matrixConfig.quadrants[quadrantId];

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item: { id: string; quadrant: string }) => {
      if (item.quadrant !== quadrantId) {
        onTaskMove(item.id, quadrantId);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const dropRef = drop as any;

  return (
    <div ref={dropRef} style={{ width: '100%' }}>
      <Paper
      elevation={2}
      sx={{
        p: { xs: 1.5, sm: 2, md: 2 },
        minHeight: { xs: 'auto', sm: '280px', md: '300px' },
        backgroundColor: isOver ? 'action.hover' : 'background.paper',
        border: `2px solid ${quadrant.color}`,
        borderStyle: isOver ? 'dashed' : 'solid',
        width: '100%',
        boxSizing: 'border-box',
        // Mobile-specific styles
        '@media (max-width: 599px)': {
          maxHeight: 'none',
          overflowY: 'visible',
          borderRadius: 2
        },
        // Desktop styles
        '@media (min-width: 600px)': {
          maxHeight: '450px',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin'
        }
      }}
    >
      <Box mb={{ xs: 1, sm: 2 }}>
        <Typography
          variant="h6"
          sx={{
            color: quadrant.color,
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}
        >
          {quadrant.title}
        </Typography>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            display: { xs: 'none', sm: 'block' }
          }}
        >
          {quadrant.subtitle}
        </Typography>
        <Chip
          label={`${tasks.length} tasks`}
          size="small"
          sx={{
            mt: { xs: 0.5, sm: 1 },
            backgroundColor: quadrant.color,
            color: 'white',
            display: 'block',
            mx: 'auto',
            width: 'fit-content',
            fontSize: { xs: '0.7rem', sm: '0.875rem' },
            height: { xs: 20, sm: 24 }
          }}
        />
      </Box>

      <Box>
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onDelete={onTaskDelete}
            onStatusChange={onTaskStatusChange}
            showArchived={showArchived}
            onArchiveToggle={onArchiveToggle}
          />
        ))}
        
        {tasks.length === 0 && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={{ xs: 100, sm: 150, md: 200 }}
            sx={{
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 1,
              color: 'text.secondary',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                textAlign: 'center',
                px: 1
              }}
            >
              Drop tasks here
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
    </div>
  );
};

// Main Eisenhower Matrix Component
const EisenhowerMatrix: React.FC<EisenhowerMatrixProps> = ({
  tasks,
  onTasksChange,
  onError,
  showArchived = false,
  onArchiveToggle,
}) => {
  const [localTasks, setLocalTasks] = useState<Task[]>(tasks);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  const handleTaskMove = async (taskId: string, newQuadrant: string) => {
    try {
      setLoading(true);
      
      // Optimistic update
      const updatedTasks = localTasks.map(task =>
        task.id === taskId ? { ...task, quadrant: newQuadrant as any } : task
      );
      setLocalTasks(updatedTasks);
      onTasksChange?.(updatedTasks);

      // Update via n8n
      await n8nService.moveTask(taskId, newQuadrant);
      
    } catch (error) {
      // Revert on error
      setLocalTasks(tasks);
      const errorMessage = error instanceof Error ? error.message : 'Failed to move task';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskDelete = async (taskId: string) => {
    try {
      setLoading(true);
      
      // Optimistic update
      const updatedTasks = localTasks.filter(task => task.id !== taskId);
      setLocalTasks(updatedTasks);
      onTasksChange?.(updatedTasks);

      // Delete via n8n
      await n8nService.deleteTask(taskId);
      
    } catch (error) {
      // Revert on error
      setLocalTasks(tasks);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete task';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskStatusChange = async (taskId: string, newStatus: Task['status']) => {
    try {
      setLoading(true);
      
      // Find the task being updated
      const taskBeingUpdated = localTasks.find(task => task.id === taskId);
      
      // Optimistic update
      const updatedTasks = localTasks.map(task =>
        task.id === taskId ? { ...task, status: newStatus } : task
      );
      setLocalTasks(updatedTasks);
      onTasksChange?.(updatedTasks);

      // Update via n8n
      await n8nService.updateTask(taskId, { status: newStatus });
      
      // Special handling for task completion
      if (newStatus === 'completed' && taskBeingUpdated) {
        // Show celebration feedback
        console.log(`🎉 Task completed: "${taskBeingUpdated.title}"`);
        
        // You can add more celebration logic here:
        // - Show a toast notification
        // - Play a sound
        // - Add confetti animation
        // - Update user stats/progress
        
        // Optional: Move completed tasks to a "completed" section after a delay
        // setTimeout(() => {
        //   // Could implement auto-archiving or moving to a completed section
        // }, 2000);
      }
      
    } catch (error) {
      // Revert on error
      setLocalTasks(tasks);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update task status';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getTasksByQuadrant = (quadrantId: keyof typeof matrixConfig.quadrants) => {
    return localTasks.filter(task => task.quadrant === quadrantId);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ px: { xs: 0, sm: 2, md: 0 }, pb: { xs: 8, sm: 2 } }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            textAlign: 'center',
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            px: { xs: 2, sm: 0 }
          }}
        >
          Eisenhower Matrix
        </Typography>

        {loading && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              mx: { xs: 2, sm: 0 },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Updating tasks...
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 2, sm: 2, md: 3 },
            width: '100%',
            px: { xs: 2, sm: 0 },
            '@media (min-width: 600px)': {
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 2
            }
          }}
        >
          <Quadrant
            quadrantId="urgent-important"
            tasks={getTasksByQuadrant('urgent-important')}
            onTaskMove={handleTaskMove}
            onTaskDelete={handleTaskDelete}
            onTaskStatusChange={handleTaskStatusChange}
            showArchived={showArchived}
            onArchiveToggle={onArchiveToggle}
          />

          <Quadrant
            quadrantId="important"
            tasks={getTasksByQuadrant('important')}
            onTaskMove={handleTaskMove}
            onTaskDelete={handleTaskDelete}
            onTaskStatusChange={handleTaskStatusChange}
            showArchived={showArchived}
            onArchiveToggle={onArchiveToggle}
          />

          <Quadrant
            quadrantId="urgent"
            tasks={getTasksByQuadrant('urgent')}
            onTaskMove={handleTaskMove}
            onTaskDelete={handleTaskDelete}
            onTaskStatusChange={handleTaskStatusChange}
            showArchived={showArchived}
            onArchiveToggle={onArchiveToggle}
          />

          <Quadrant
            quadrantId="neither"
            tasks={getTasksByQuadrant('neither')}
            onTaskMove={handleTaskMove}
            onTaskDelete={handleTaskDelete}
            onTaskStatusChange={handleTaskStatusChange}
            showArchived={showArchived}
            onArchiveToggle={onArchiveToggle}
          />
        </Box>
      </Box>
    </DndProvider>
  );
};

export default EisenhowerMatrix;
